import { useState, useEffect } from 'react';
import API from './api';
import { pollInterval } from './livePoll';

/**
 * One copy of this week's games, for every surface that shows them.
 *
 * Matching the poll RATES wasn't enough. The ticker and the picks cards each ran
 * their own 15-second timer, started whenever that component happened to mount,
 * so they sat at an arbitrary offset from each other — and during a live game
 * the clock on a card and the clock in the banner above it read a quarter of a
 * minute apart. Two requests for the same data, arriving at different moments,
 * can only agree by luck.
 *
 * So there is one timer and one copy. Every subscriber renders the same object
 * at the same time, and they cannot drift by construction rather than by
 * carefully keeping two numbers in step.
 *
 * The timer only runs while something is subscribed, and its interval is
 * recomputed after each load, so it speeds up by itself at kickoff and goes
 * quiet again when the day is done.
 */

let state = { games: [], week: null, loaded: false };
let subscribers = new Set();
let timer = null;
let inFlight = null;

function emit() {
  for (const fn of subscribers) fn(state);
}

async function load() {
  // Never two at once: a slow response would otherwise let a later, faster one
  // land first and be overwritten by stale data.
  if (inFlight) return inFlight;
  inFlight = API.get('/games')
    .then(r => {
      state = { games: r.data.games || [], week: r.data.week || null, loaded: true };
      emit();
    })
    .catch(() => { /* keep the last good copy rather than blanking the screen */ })
    .finally(() => { inFlight = null; });
  return inFlight;
}

function schedule() {
  if (timer) clearTimeout(timer);
  if (!subscribers.size) { timer = null; return; }
  timer = setTimeout(async () => {
    await load();
    schedule();
  }, pollInterval(state.games));
}

/** Ask for a fresh copy now — after saving picks, say. */
export function refreshGames() {
  return load().then(schedule);
}

/**
 * The current copy, fetching it first if nobody has yet.
 *
 * For the pages that need games inside an async load rather than as a
 * subscription — they want the data in hand to sequence the next call. Callers
 * that arrive while a request is already in flight wait for that one instead of
 * starting a second.
 */
export async function ensureGames() {
  if (!state.loaded || inFlight) await load();
  return state;
}

export function useGames() {
  const [snapshot, setSnapshot] = useState(state);

  useEffect(() => {
    subscribers.add(setSnapshot);
    // First subscriber starts the loop; later ones get the copy already loaded
    // rather than firing another request of their own.
    if (!state.loaded) load().then(schedule);
    else if (!timer) schedule();

    return () => {
      subscribers.delete(setSnapshot);
      if (!subscribers.size && timer) { clearTimeout(timer); timer = null; }
    };
  }, []);

  return snapshot;
}
